import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import get from 'lodash-es/get';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import {
  useFetchMultipleProductDetails,
  useFetchProductsByIds,
} from '@jane/business-admin/data-access';
import type { MenuProductForProductsTable } from '@jane/business-admin/types';

export const useGetProductDetails = (
  product_ids: number[],
  useMenuProductId = false,
  enabled = true
) => {
  const { id: storeId = '' } = useParams<'id'>();
  const isBatchEnabled = useFlag(FLAGS.c3BatchProductDetailFetch);

  const productIdsAsStrings = useMemo(
    () => [...(product_ids ?? [])].sort().map((id) => id.toString()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(product_ids)]
  );

  const batchQuery = useFetchProductsByIds(
    { storeId, ids: productIdsAsStrings, useMenuProductId },
    { enabled: isBatchEnabled && enabled }
  );

  // Legacy path: one request per id. Disabled when the batch flag is on.
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [data, setData] = useState<MenuProductForProductsTable[]>();
  const productsQueries = useFetchMultipleProductDetails(
    storeId,
    product_ids || [],
    useMenuProductId,
    !isBatchEnabled && enabled
  );

  useEffect(() => {
    if (isBatchEnabled) return;

    const totalQueries = product_ids?.length || 0;
    let finishedQueries = 0;
    const returnedProducts = productsQueries.flatMap((query) => {
      if (query.isError) {
        const statusCode = get(query, 'failureReason.response.status', 0);
        if (statusCode.toString().startsWith('5')) {
          setHasError(true);
        }
      }
      if (query.isLoading) {
        setIsLoading(true);
      }
      if (query.isFetched) {
        finishedQueries++;
        if (finishedQueries === totalQueries) {
          setIsLoading(false);
        }
        if (!query.data?.product) {
          return [];
        }
        const product = query.data?.product;
        return product;
      }
      return [];
    });

    setData(returnedProducts);
  }, [JSON.stringify(productsQueries), product_ids?.length, isBatchEnabled]);

  if (isBatchEnabled) {
    const batchStatusCode = get(batchQuery, 'failureReason.response.status', 0);
    const batchHasError =
      batchQuery.isError && batchStatusCode.toString().startsWith('5');

    return {
      data: batchQuery.data?.products,
      hasError: batchHasError,
      isLoading: batchQuery.isLoading,
    };
  }

  return { data, hasError, isLoading };
};
